import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { Link } from "react-router-dom"; // Import Link from react-router-dom
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useUserContext } from "../../providers/userProvider";
import { useNavigate } from "react-router-dom";
import { useCustomThemeContext } from "../../providers/customThemeProvider";

// Define the LoginPage functional component
export const LoginPage: FC = () => {
  const { updateTheme } = useCustomThemeContext();

  // Update the theme when the component mounts
  useEffect(() => {
    updateTheme();
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const { signin } = useUserContext();
  const navigate = useNavigate();

  // Function to handle showing/hiding the password
  const handleClickShowPassword = () => setShowPassword(!showPassword);

  // Function to handle mouse down event on the password visibility toggle
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  // Function to handle the sign-in process
  const onSignin = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const result = await signin(email, password);
      if (result.error) {
        setError(result.error);
      } else if (result.user) {
        navigate("/");
      }
    } catch (err) {
      setError("Failed to sign in");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: 1,
        minHeight: "84vh",
        background: "#e2e2e2",
      }}
    >
      <Box
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        justifyContent={"center"}
        flexGrow={1}
        p={3}
        boxShadow={3}
      >
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          justifyContent={"center"}
          p={5}
          width={1}
          maxWidth={350}
          borderRadius={4}
          sx={{ background: "white" }}
        >
          <Typography variant="h4" gutterBottom>
            Login
          </Typography>
          <Box component="form" onSubmit={onSignin} sx={{ mt: 1 }}>
            <TextField
              variant="outlined"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              fullWidth
              margin="normal"
              required
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {/* Display error message if there is an error */}
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
            {/* Login button */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              onClick={onSignin}
            >
              Login
            </Button>
            {/* Link to the forgot password page */}
            <Typography variant="body2" sx={{ mt: 2 }}>
              Forgot your password?{" "}
              <Link to="/forgot-password">Reset it here</Link>
            </Typography>
            {/* Link to the registration page */}
            <Typography variant="body2" sx={{ mt: 2 }}>
              Not registered yet?{" "}
              <Link to="/register">Click here to register!</Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
